import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Badge,
    Box,
    Button,
    Flex,
    Grid,
    HStack,
    Icon,
    IconButton,
    Menu,
    MenuButton,
    MenuItem,
    MenuList,
    SimpleGrid,
    Stat,
    StatHelpText,
    StatLabel,
    StatNumber,
    Tab,
    TabList,
    TabPanel,
    TabPanels,
    Tabs,
    Tag,
    Text,
    Wrap,
    WrapItem,
    useColorModeValue,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import {
    MdAdd,
    MdContentCopy,
    MdDelete,
    MdEdit,
    MdFitnessCenter,
    MdGraphicEq,
    MdMoreVert,
    MdPlayArrow,
    MdRepeat,
    MdSchedule,
    MdSpeed,
    MdTimer,
} from "react-icons/md";
import {fetchTunes, loadRoutines, removeRoutine, upsertRoutine} from "./Services";
import RoutineBuilderModal, {
    PT_CATEGORIES,
    WEEK_DAYS,
    formatDuration,
    routineSeconds,
} from "./RoutineBuilderModal";
import PTPlayer from "./PTPlayer";
import SequencePlayer from "./SequencePlayer";
import {
    SAMPLE_SEQUENCES,
    SEQUENCE_TYPES,
    formatDuration as formatSeqDuration,
    sequenceDuration,
    sequenceInstruments,
    typeMeta,
} from "./SampleSequences";
import {INSTRUMENTS} from "./ptAudio";

const categoryMeta = (value) => PT_CATEGORIES.find((c) => c.value === value) || {label: value, color: "gray"};

const uid = () => `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

const scheduleSummary = (routine) => {
    const days = routine?.schedule?.days || [];
    if (days.length === 0) return "No days set";
    if (days.length === 7) return "Every day";
    const ordered = WEEK_DAYS.filter((d) => days.includes(d.value)).map((d) => d.label);
    return ordered.join(", ");
};

/* ----------------------- sample sequence card ----------------------- */

// Compact preview of a sequence's rhythm: one dot row per instrument, filled
// dots on the beats that play, brighter on accents.
function PatternPreview({sequence}) {
    return (
        <Box>
            {sequenceInstruments(sequence).map((inst) => {
                const info = INSTRUMENTS[inst] || {color: "gray"};
                return (
                    <HStack key={inst} spacing="3px" mb="3px">
                        {sequence.pattern[inst].map((value, i) => (
                            <Box
                                key={i}
                                w="9px"
                                h="9px"
                                borderRadius="2px"
                                bg={value > 0 ? `${info.color}.${value === 2 ? "400" : "300"}` : "gray.200"}
                            />
                        ))}
                    </HStack>
                );
            })}
        </Box>
    );
}

function SampleSequenceCard({sequence, cardBg, subtle, onPlay}) {
    const meta = typeMeta(sequence.type);
    return (
        <Box
            bg={cardBg}
            borderRadius="18px"
            boxShadow="sm"
            border="1px solid"
            borderColor="transparent"
            overflow="hidden"
            transition="all 0.2s ease"
            _hover={{transform: "translateY(-4px)", boxShadow: "lg", borderColor: `${meta.color}.200`}}
            display="flex"
            flexDirection="column"
        >
            <Box h="6px" bgGradient={`linear(to-r, ${meta.color}.400, ${meta.color}.200)`}/>
            <Box p={5} flex="1" display="flex" flexDirection="column">
                <Flex justify="space-between" align="center" mb={3}>
                    <HStack spacing={3}>
                        <Flex w="42px" h="42px" borderRadius="12px" bg={`${meta.color}.50`} color={`${meta.color}.500`}
                              align="center" justify="center">
                            <Icon as={sequence.icon || MdGraphicEq} boxSize="24px"/>
                        </Flex>
                        <Box>
                            <Text fontWeight="bold" noOfLines={1}>{sequence.name}</Text>
                            <Badge colorScheme={meta.color} borderRadius="full" fontSize="2xs">{meta.label}</Badge>
                        </Box>
                    </HStack>
                </Flex>

                <Text color={subtle} fontSize="sm" noOfLines={2} minH="40px">{sequence.description}</Text>

                <Box my={4}>
                    <PatternPreview sequence={sequence}/>
                </Box>

                <HStack spacing={4} color={subtle} fontSize="sm" flexWrap="wrap" mt="auto">
                    <HStack spacing={1}><Icon as={MdSpeed}/><Text>{sequence.bpm} BPM</Text></HStack>
                    <HStack spacing={1}><Icon as={MdRepeat}/><Text>{sequence.defaultReps}×</Text></HStack>
                    <HStack spacing={1}><Icon as={MdTimer}/><Text>{formatSeqDuration(sequenceDuration(sequence))}</Text></HStack>
                </HStack>

                <Button mt={4} w="full" colorScheme={meta.color} leftIcon={<MdPlayArrow/>} onClick={() => onPlay(sequence)}>
                    Play cadence
                </Button>
            </Box>
        </Box>
    );
}

function PhysicalTraining() {
    const toast = useToast();
    const builder = useDisclosure();
    const player = useDisclosure();
    const sequencePlayer = useDisclosure();

    const cardBg = useColorModeValue("white", "gray.700");
    const pageBg = useColorModeValue("gray.50", "gray.900");
    const subtle = useColorModeValue("gray.500", "gray.400");

    const [routines, setRoutines] = useState([]);
    const [tunes, setTunes] = useState([]);
    const [filter, setFilter] = useState("ALL");
    const [seqFilter, setSeqFilter] = useState("ALL");
    const [editing, setEditing] = useState(null);
    const [playing, setPlaying] = useState(null);
    const [playingSequence, setPlayingSequence] = useState(null);

    const refresh = useCallback(() => setRoutines(loadRoutines()), []);

    useEffect(() => {
        refresh();
        fetchTunes().then(setTunes);
    }, [refresh]);

    const filtered = useMemo(
        () => (filter === "ALL" ? routines : routines.filter((r) => r.category === filter)),
        [routines, filter]
    );

    const filteredSequences = useMemo(
        () => (seqFilter === "ALL" ? SAMPLE_SEQUENCES : SAMPLE_SEQUENCES.filter((s) => s.type === seqFilter)),
        [seqFilter]
    );

    const stats = useMemo(() => {
        const exercises = routines.reduce((sum, r) => sum + (r.exercises?.length || 0), 0);
        const seconds = routines.reduce((sum, r) => sum + routineSeconds(r), 0);
        const scheduled = routines.filter((r) => r.enabled && (r.schedule?.days?.length || 0) > 0).length;
        return {total: routines.length, exercises, seconds, scheduled};
    }, [routines]);

    const openNew = () => {
        setEditing(null);
        builder.onOpen();
    };
    const openEdit = (routine) => {
        setEditing(routine);
        builder.onOpen();
    };

    const handleSave = (routine) => {
        setRoutines(upsertRoutine(routine));
        toast({title: editing ? "Routine updated" : "Routine created", status: "success", duration: 2500, isClosable: true});
        builder.onClose();
    };

    const handleDelete = (routine) => {
        setRoutines(removeRoutine(routine.id));
        toast({title: "Routine deleted", status: "info", duration: 2000, isClosable: true});
    };

    const handleDuplicate = (routine) => {
        const copy = {
            ...JSON.parse(JSON.stringify(routine)),
            id: uid(),
            name: `${routine.name} (copy)`,
            createdAt: Date.now(),
        };
        setRoutines(upsertRoutine(copy));
        toast({title: "Routine duplicated", status: "success", duration: 2000, isClosable: true});
    };

    const startPlayer = (routine) => {
        if (!routine.exercises?.length) {
            toast({title: "This routine has no exercises", status: "warning", duration: 2500, isClosable: true});
            return;
        }
        setPlaying(routine);
        player.onOpen();
    };

    const startSequence = (sequence) => {
        setPlayingSequence(sequence);
        sequencePlayer.onOpen();
    };

    return (
        <Flex direction="column" pt={{base: "110px", md: "80px"}} px={{base: "4", md: "8"}} pb="60px" bg={pageBg} minH="100vh">
            {/* header */}
            <Flex justify="space-between" align={{base: "flex-start", md: "center"}} direction={{base: "column", md: "row"}} gap={4} mb={6}>
                <HStack spacing={3}>
                    <Flex w="50px" h="50px" borderRadius="14px" bgGradient="linear(to-br, teal.400, green.400)" color="white" align="center" justify="center">
                        <Icon as={MdFitnessCenter} boxSize="28px"/>
                    </Flex>
                    <Box>
                        <Text fontSize="2xl" fontWeight="bold">Physical Training (PT)</Text>
                        <Text color={subtle}>Play ready-made PT sound cadences or build your own scheduled routines.</Text>
                    </Box>
                </HStack>
            </Flex>

            <Tabs colorScheme="teal" variant="soft-rounded" isLazy>
                <TabList mb={6} gap={2} flexWrap="wrap">
                    <Tab borderRadius="full">
                        <Icon as={MdGraphicEq} mr={2}/> Sample Sequences
                    </Tab>
                    <Tab borderRadius="full">
                        <Icon as={MdFitnessCenter} mr={2}/> My Routines
                    </Tab>
                </TabList>

                <TabPanels>
                    {/* ------------------ Sample sound sequences ------------------ */}
                    <TabPanel p={0}>
                        <Flex justify="space-between" align={{base: "flex-start", md: "center"}}
                              direction={{base: "column", md: "row"}} gap={3} mb={5}>
                            <Box>
                                <Text fontWeight="bold" fontSize="lg">PT Drum &amp; Tune Sequences</Text>
                                <Text color={subtle} fontSize="sm">
                                    Tap a cadence to play it live — adjust tempo and repetitions on the fly.
                                </Text>
                            </Box>
                        </Flex>

                        {/* type filters */}
                        <Wrap spacing={2} mb={6}>
                            <WrapItem>
                                <Button size="sm" borderRadius="full" variant={seqFilter === "ALL" ? "solid" : "outline"}
                                        colorScheme={seqFilter === "ALL" ? "teal" : "gray"} onClick={() => setSeqFilter("ALL")}>
                                    All
                                </Button>
                            </WrapItem>
                            {SEQUENCE_TYPES.map((t) => (
                                <WrapItem key={t.value}>
                                    <Button size="sm" borderRadius="full" variant={seqFilter === t.value ? "solid" : "outline"}
                                            colorScheme={seqFilter === t.value ? t.color : "gray"} onClick={() => setSeqFilter(t.value)}>
                                        {t.label}
                                    </Button>
                                </WrapItem>
                            ))}
                        </Wrap>

                        <Grid templateColumns={{base: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr"}} gap={5}>
                            {filteredSequences.map((sequence) => (
                                <SampleSequenceCard
                                    key={sequence.id}
                                    sequence={sequence}
                                    cardBg={cardBg}
                                    subtle={subtle}
                                    onPlay={startSequence}
                                />
                            ))}
                        </Grid>
                    </TabPanel>

                    {/* ------------------------ My routines ----------------------- */}
                    <TabPanel p={0}>
                        <Flex justify="space-between" align={{base: "flex-start", md: "center"}}
                              direction={{base: "column", md: "row"}} gap={3} mb={5}>
                            <Box>
                                <Text fontWeight="bold" fontSize="lg">My PT Routines</Text>
                                <Text color={subtle} fontSize="sm">
                                    Design drills with tunes, reps &amp; timers, then schedule and play them live.
                                </Text>
                            </Box>
                            <Button colorScheme="teal" leftIcon={<MdAdd/>} onClick={openNew} borderRadius="12px">
                                New Routine
                            </Button>
                        </Flex>

                        {/* stats */}
                        <SimpleGrid columns={{base: 2, md: 4}} spacing={4} mb={6}>
                            <Stat bg={cardBg} p={4} borderRadius="14px" boxShadow="sm">
                                <StatLabel color={subtle}>Routines</StatLabel>
                                <StatNumber>{stats.total}</StatNumber>
                            </Stat>
                            <Stat bg={cardBg} p={4} borderRadius="14px" boxShadow="sm">
                                <StatLabel color={subtle}>Exercises</StatLabel>
                                <StatNumber>{stats.exercises}</StatNumber>
                            </Stat>
                            <Stat bg={cardBg} p={4} borderRadius="14px" boxShadow="sm">
                                <StatLabel color={subtle}>Total time</StatLabel>
                                <StatNumber>{formatDuration(stats.seconds)}</StatNumber>
                            </Stat>
                            <Stat bg={cardBg} p={4} borderRadius="14px" boxShadow="sm">
                                <StatLabel color={subtle}>Scheduled</StatLabel>
                                <StatNumber>{stats.scheduled}</StatNumber>
                                <StatHelpText mb={0}>active this week</StatHelpText>
                            </Stat>
                        </SimpleGrid>

                        {/* filters */}
                        <Wrap spacing={2} mb={6}>
                            <WrapItem>
                                <Button size="sm" borderRadius="full" variant={filter === "ALL" ? "solid" : "outline"}
                                        colorScheme={filter === "ALL" ? "teal" : "gray"} onClick={() => setFilter("ALL")}>
                                    All
                                </Button>
                            </WrapItem>
                            {PT_CATEGORIES.map((c) => (
                                <WrapItem key={c.value}>
                                    <Button size="sm" borderRadius="full" variant={filter === c.value ? "solid" : "outline"}
                                            colorScheme={filter === c.value ? c.color : "gray"} onClick={() => setFilter(c.value)}>
                                        {c.label}
                                    </Button>
                                </WrapItem>
                            ))}
                        </Wrap>

                        {/* list */}
                        {filtered.length === 0 ? (
                            <Flex direction="column" align="center" justify="center" bg={cardBg} borderRadius="18px" boxShadow="sm" py="90px" textAlign="center">
                                <Flex w="80px" h="80px" borderRadius="20px" bg="teal.50" color="teal.400" align="center" justify="center" mb={4}>
                                    <Icon as={MdFitnessCenter} boxSize="42px"/>
                                </Flex>
                                <Text fontSize="lg" fontWeight="bold">
                                    {routines.length === 0 ? "No PT routines yet" : "Nothing in this category"}
                                </Text>
                                <Text color={subtle} mt={1} mb={5}>
                                    Build a sequence of exercises with tunes, reps and timers for your school assembly.
                                </Text>
                                <Button colorScheme="teal" leftIcon={<MdAdd/>} onClick={openNew}>Create your first routine</Button>
                            </Flex>
                        ) : (
                            <Grid templateColumns={{base: "1fr", md: "1fr 1fr", xl: "1fr 1fr 1fr"}} gap={5}>
                                {filtered.map((routine) => {
                                    const meta = categoryMeta(routine.category);
                                    return (
                                        <Box
                                            key={routine.id}
                                            bg={cardBg}
                                            borderRadius="18px"
                                            boxShadow="sm"
                                            border="1px solid"
                                            borderColor="transparent"
                                            overflow="hidden"
                                            transition="all 0.2s ease"
                                            _hover={{transform: "translateY(-4px)", boxShadow: "lg", borderColor: `${meta.color}.200`}}
                                            display="flex"
                                            flexDirection="column"
                                        >
                                            <Box h="6px" bgGradient={`linear(to-r, ${meta.color}.400, ${meta.color}.200)`}/>
                                            <Box p={5} flex="1" display="flex" flexDirection="column">
                                                <Flex justify="space-between" align="flex-start" mb={2}>
                                                    <Badge colorScheme={meta.color} borderRadius="full" px={3} py={1}>{meta.label}</Badge>
                                                    <HStack spacing={1}>
                                                        {!routine.enabled && <Tag size="sm" colorScheme="gray">Draft</Tag>}
                                                        <Menu placement="bottom-end">
                                                            <MenuButton as={IconButton} aria-label="More" icon={<MdMoreVert/>} size="sm" variant="ghost"/>
                                                            <MenuList minW="160px">
                                                                <MenuItem icon={<MdEdit/>} onClick={() => openEdit(routine)}>Edit</MenuItem>
                                                                <MenuItem icon={<MdContentCopy/>} onClick={() => handleDuplicate(routine)}>Duplicate</MenuItem>
                                                                <MenuItem icon={<MdDelete/>} color="red.500" onClick={() => handleDelete(routine)}>Delete</MenuItem>
                                                            </MenuList>
                                                        </Menu>
                                                    </HStack>
                                                </Flex>

                                                <Text fontSize="lg" fontWeight="bold" noOfLines={1}>{routine.name}</Text>
                                                {routine.description && (
                                                    <Text color={subtle} fontSize="sm" noOfLines={2} mt={1}>{routine.description}</Text>
                                                )}

                                                <HStack spacing={4} mt={4} color={subtle} fontSize="sm" flexWrap="wrap">
                                                    <HStack spacing={1}><Icon as={MdFitnessCenter}/><Text>{routine.exercises?.length || 0} moves</Text></HStack>
                                                    <HStack spacing={1}><Icon as={MdTimer}/><Text>{formatDuration(routineSeconds(routine))}</Text></HStack>
                                                    <HStack spacing={1}>
                                                        <Icon as={MdRepeat}/>
                                                        <Text>{(routine.exercises || []).reduce((s, e) => s + (Number(e.reps) || 0), 0)} reps</Text>
                                                    </HStack>
                                                </HStack>

                                                <HStack spacing={1} mt={2} color={subtle} fontSize="sm">
                                                    <Icon as={MdSchedule}/>
                                                    <Text noOfLines={1}>
                                                        {scheduleSummary(routine)}
                                                        {routine.schedule?.time ? ` · ${routine.schedule.time}` : ""}
                                                    </Text>
                                                </HStack>

                                                <Button
                                                    mt={5}
                                                    w="full"
                                                    colorScheme={meta.color}
                                                    leftIcon={<MdPlayArrow/>}
                                                    onClick={() => startPlayer(routine)}
                                                >
                                                    Start Session
                                                </Button>
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Grid>
                        )}
                    </TabPanel>
                </TabPanels>
            </Tabs>

            <RoutineBuilderModal
                isOpen={builder.isOpen}
                onClose={builder.onClose}
                onSave={handleSave}
                tunes={tunes}
                initial={editing}
            />
            <PTPlayer isOpen={player.isOpen} onClose={player.onClose} routine={playing}/>
            <SequencePlayer isOpen={sequencePlayer.isOpen} onClose={sequencePlayer.onClose} sequence={playingSequence}/>
        </Flex>
    );
}

export default PhysicalTraining;
